import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Siren, Plus, Clock } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const STATUS_STYLE = {
  draft: "bg-gray-100 text-gray-700 border-gray-300",
  returned: "bg-amber-100 text-amber-800 border-amber-300",
  in_review: "bg-blue-100 text-blue-800 border-blue-300",
  approved: "bg-violet-100 text-violet-800 border-violet-300",
  filed: "bg-green-100 text-green-800 border-green-300",
};
const ALERT_STYLE = {
  emergency: "bg-red-100 text-red-800 border-red-300",
  normal: "bg-orange-100 text-orange-800 border-orange-300",
  attempted_cyber: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  system: "bg-sky-100 text-sky-800 border-sky-300",
};

export default function Eop004() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { isSubHidden } = useModuleVisibility();

  const load = () => api.get("/eop004/events").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/eop004/events", { name });
      toast.success("Event report created");
      setOpen(false); setName("");
      nav(`/eop004/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create event");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <PageHeader title="EOP-004 Event Reporting"
          subtitle="Draft, review and approve DOE-417 Electric Emergency Incident & Disturbance Reports — then file them manually with DOE, NERC, E-ISAC and CISA. This tool never submits to DOE; a person always files." />
        {!isSubHidden("eop004:new") && <Button data-testid="eop004-new-btn" onClick={() => setOpen(true)} className="shrink-0">
          <Plus size={16} className="mr-1.5" /> New Event
        </Button>}
      </div>

      {rows === null ? null : rows.length === 0 ? (
        <Empty title="No event reports yet"
          hint="Click 'New Event' to start a DOE-417. As you check the Schedule 1 criteria, the alert status and filing deadline are set automatically." />
      ) : (
        <div className="border border-border" data-testid="eop004-list">
          <table className="text-sm w-full">
            <thead className="bg-secondary">
              <tr>{["Event", "Alert Status", "Filing deadline", "Status", "Created", ""].map((h) => (
                <th key={h} className="px-4 py-2 text-left font-semibold">{h}</th>))}</tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                  data-testid={`eop004-row-${e.id}`} onClick={() => nav(`/eop004/${e.id}`)}>
                  <td className="px-4 py-3 font-medium">{e.name}</td>
                  <td className="px-4 py-3">
                    {e.alert_status ? (
                      <span className={`text-xs border px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${ALERT_STYLE[e.alert_status] || ""}`}>
                        <Siren size={11} weight="fill" /> {e.alert_label}
                      </span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {e.deadline_primary ? (
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> {e.deadline_primary.replace("T", " ").slice(0, 16)}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs border px-1.5 py-0.5 rounded ${STATUS_STYLE[e.status] || STATUS_STYLE.draft}`}>
                      {(e.status || "draft").replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">{(e.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-muted-foreground"><Siren size={16} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="eop004-new-dialog">
          <DialogHeader><DialogTitle>New DOE-417 event report</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="eop004-name">Event name / reference</Label>
            <Input id="eop004-name" data-testid="eop004-name-input" value={name}
              onChange={(e) => setName(e.target.value)} placeholder="e.g., Substation physical attack — 06/15" />
            <p className="text-xs text-muted-foreground">You can rename this later. Next you'll check the Schedule 1 criteria that apply.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="eop004-create-btn" onClick={create} disabled={busy}>
              {busy ? "Creating…" : "Create Event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
